import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';

export interface IFormProps extends IBaseElementProps {
	useBackground?: boolean;
	isCentered?: boolean;
}

/**
 * Form
 * @block
 * @requiresHooks
 * @icon feedback
 */
const Form: React.FC<IFormProps> = ({
	className,
	classNames = [],
	children,
	useBackground = false,
	isCentered = false,
}) => {
	const ref = useRef<HTMLDivElement>(null);

	// Adds focus classes to fields wrappers
	useEffect(() => {
		let fields = ref.current?.querySelectorAll(
			'input, textarea, select',
		) as NodeListOf<HTMLInputElement>;

		const handleFocus = (event: FocusEvent) => {
			(event.target as HTMLElement)
				.closest('.gfield')
				.classList.add('input-focused');
		};

		const handleBlur = (event: FocusEvent) => {
			const target = event.target as HTMLInputElement;
			const value = target.value;
			if (target.type === 'tel') {
				if (value === '(___) ___-____' || value === '') {
					(event.target as HTMLElement)
						.closest('.gfield')
						.classList.remove('input-focused');
				}
			} else {
				if (!value) {
					(event.target as HTMLElement)
						.closest('.gfield')
						.classList.remove('input-focused');
				}
			}
		};

		const handleChange = (event: any) => {
			const target = event.target as HTMLInputElement;
			const { value } = target;
			const wrapper = target.closest('.gfield');
			if (value) {
				wrapper?.classList.add('input-filled');
			} else {
				wrapper?.classList.remove('input-filled');
			}
		};

		const handleFocusClasses = () => {
			fields = ref.current?.querySelectorAll(
				'input, textarea, select',
			) as NodeListOf<HTMLInputElement>;

			const jQuery = window['jQuery'] || undefined;
			if (jQuery !== undefined) {
				jQuery(document).on('gform_post_render', () => {
					fields &&
						fields.forEach((field) => {
							if (field.tagName.toLowerCase() === 'select') {
								field
									.closest('.gfield')
									.classList.add('gfield--select');
							}
							if (
								field.tagName.toLowerCase() === 'input' &&
								field.type === 'email'
							) {
								field
									.closest('.gfield')
									.classList.add('gfield--email');
							}

							field.addEventListener('focus', handleFocus);
							field.addEventListener('blur', handleBlur);
							field.addEventListener('change', handleChange);
							handleChange({
								target: field,
							});
						});
				});
			}
			// Adding else condition so that from fields labels display correctly in Storybook as well as withing WordPress
			else {
				fields &&
					fields.forEach((field) => {
						if (field.tagName.toLowerCase() === 'select') {
							field
								.closest('.gfield')
								.classList.add('gfield--select');
						}
						if (
							field.tagName.toLowerCase() === 'input' &&
							field.type === 'email'
						) {
							field
								.closest('.gfield')
								.classList.add('gfield--email');
						}
						field.addEventListener('focus', handleFocus);
						field.addEventListener('blur', handleBlur);
						field.addEventListener('change', handleChange);
						handleChange({
							target: field,
						});
					});
			}
		};

		const document = window.document;
		document.addEventListener('DOMContentLoaded', handleFocusClasses);

		return () => {
			document.removeEventListener(
				'DOMContentLoaded',
				handleFocusClasses,
			);

			fields &&
				fields.forEach((field) => {
					field.removeEventListener('focus', handleFocus);
					field.removeEventListener('blur', handleBlur);
					field.removeEventListener('change', handleChange);
				});
		};
	}, []);

	return (
		<div
			className={
				className ||
				cn(
					'form-wrapper',
					useBackground && 'form-wrapper--background',
					isCentered && 'form-wrapper--centered',
					...classNames,
				)
			}
			ref={ref}
		>
			{children}
		</div>
	);
};

export default Form;
